import React, { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { staffApi, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mail, RefreshCw, ShieldCheck } from "lucide-react";

/**
 * Shared "confirm with an emailed code" panel (Jun 2026).
 *
 * Replaces every password re-entry gate: manager approval of a
 * void/refund/price override, report exports and Launch Reset. The code
 * always goes to the authorising person's own inbox — it is never shown
 * on screen.
 *
 * `askEmail` lets a DIFFERENT manager authorise on a colleague's till.
 * When false the code is sent to the signed-in user's own email.
 * `onVerified({ pendingId, code })` hands the verified pair back so the
 * caller can post it to its own endpoint.
 */
export const ReauthCodeFields = ({
  purpose,
  askEmail = false,
  email,
  setEmail,
  pendingId,
  setPendingId,
  code,
  setCode,
  busy,
  setBusy,
  idPrefix = "reauth",
}) => {
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setInterval(() => setCooldown((n) => (n <= 1 ? 0 : n - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const sendCode = async () => {
    setBusy(true);
    try {
      const body = { purpose };
      if (askEmail) body.email = (email || "").trim().toLowerCase();
      const r = await staffApi.post("/auth/staff/reauth/request", body);
      setPendingId(r.data.pending_id);
      setCooldown(60);
      setCode("");
      if (r.data.dev_otp) toast.info(`Dev mode — code: ${r.data.dev_otp}`, { duration: 8000 });
      else toast.success(`Code sent to ${r.data.email}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      {askEmail ? (
        <div>
          <Label htmlFor={`${idPrefix}-email`} className="text-xs">Manager email</Label>
          <div className="relative mt-1">
            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              id={`${idPrefix}-email`}
              type="email"
              value={email}
              autoFocus
              onChange={(e) => setEmail(e.target.value)}
              placeholder="manager@cocoonmedicalspa.com"
              className="pl-9"
              autoComplete="off"
              data-testid={`${idPrefix}-email`}
            />
          </div>
        </div>
      ) : null}

      {!pendingId ? (
        <Button
          type="button"
          onClick={sendCode}
          disabled={busy || (askEmail && !(email || "").includes("@"))}
          className="w-full"
          data-testid={`${idPrefix}-send-code`}
        >
          {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Sending…</> : "Email me a code"}
        </Button>
      ) : (
        <div className="space-y-2">
          <Label className="text-xs">6-digit code from the email</Label>
          <InputOTP
            maxLength={6}
            value={code}
            onChange={(v) => setCode(v.replace(/\D/g, ""))}
            autoFocus
            containerClassName="gap-2"
            data-testid={`${idPrefix}-otp`}
          >
            <InputOTPGroup className="gap-2">
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <InputOTPSlot key={i} index={i} className="h-11 w-10 text-base rounded-md border first:rounded-md last:rounded-md" data-testid={`${idPrefix}-otp-slot-${i}`} />
              ))}
            </InputOTPGroup>
          </InputOTP>
          <button
            type="button"
            onClick={sendCode}
            disabled={busy || cooldown > 0}
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 disabled:cursor-not-allowed"
            data-testid={`${idPrefix}-resend`}
          >
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
            {cooldown > 0 ? `Send a new code in ${cooldown}s` : "Send a new code"}
          </button>
        </div>
      )}
    </div>
  );
};

export default function ReauthCodeDialog({
  open,
  onOpenChange,
  purpose = "export",
  title = "Confirmation code required",
  description = "We'll email a one-time code to confirm this action. The attempt is logged in the audit trail either way.",
  confirmLabel = "Verify & continue",
  onVerified,
}) {
  const [email, setEmail] = useState("");
  const [pendingId, setPendingId] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setPendingId(""); setCode(""); setEmail(""); }
  }, [open]);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await onVerified({ pendingId, code: code.trim() });
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reauth-code-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <ReauthCodeFields
            purpose={purpose}
            email={email}
            setEmail={setEmail}
            pendingId={pendingId}
            setPendingId={setPendingId}
            code={code}
            setCode={setCode}
            busy={busy}
            setBusy={setBusy}
            idPrefix="reauth"
          />
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy} data-testid="reauth-cancel">Cancel</Button>
            <Button type="submit" disabled={busy || !pendingId || code.trim().length !== 6} data-testid="reauth-submit">
              {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Verifying…</> : confirmLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
